import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import {Alert, FormControl, InputLabel, MenuItem, Select} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import downloadCsv from "../../lib/downloadCsv";
import {toast} from "react-toastify";

type AppsEmailEncoding = "SHA256" | "SHA256_BASE64";

export default function AppsEmailDownloadPage() {
    const [dateRange, setDateRange] = React.useState(RangeToTimes("Last 12 Month"));
    const [encoding, setEncoding] = React.useState<AppsEmailEncoding>("SHA256_BASE64");
    const [loading, setLoading] = React.useState(false);
    const [lastCount, setLastCount] = React.useState<number | null>(null);

    React.useEffect(() => {
        document.title = "Apps+ Email Download | DN";
    }, []);

    const runDownload = async () => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        setLastCount(null);
        try {
            const q = gql`
                query AppsEmailDownload($input: AppsEmailDownloadInput!) {
                    appsEmailDownload(input: $input)
                }
            `;
            const variables = {
                input: {
                    startDate: moment(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
                    endDate: moment(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
                    encoding,
                },
            };
            const data = await query(q, variables);
            const rows = (data?.appsEmailDownload as string[]) || [];
            setLastCount(rows.length);
            if (!rows.length) {
                toast.warning("No emails found");
            } else {
                await downloadCsv("apps_emails.csv", [["email"], ...rows.map((e) => [e])]);
                toast.success(`Downloaded ${rows.length.toLocaleString()} email(s)`);
            }
        } catch (e: any) {
            toast.error(e?.message || "Failed to download emails");
        }
        setLoading(false);
    };

    return (
        <>
            <h2>Apps+ Email Download</h2>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1, mb: 2}}>
                <DateRange showTime={true} dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: 8}}/>
                <FormControl size="small" sx={{minWidth: 180}}>
                    <InputLabel id="apps-email-encoding-label">Encoding</InputLabel>
                    <Select
                        labelId="apps-email-encoding-label"
                        label="Encoding"
                        value={encoding}
                        onChange={(e) => setEncoding(e.target.value as AppsEmailEncoding)}
                    >
                        <MenuItem value="SHA256_BASE64">SHA256+BASE64</MenuItem>
                        <MenuItem value="SHA256">SHA256</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" color="success" onClick={runDownload} disabled={loading}>
                    Download
                </Button>
            </Box>

            {loading ? (
                <Loading/>
            ) : lastCount == null ? (
                <Alert severity="info">Select a date range and encoding, then click Download.</Alert>
            ) : lastCount === 0 ? (
                <Alert severity="warning">No emails found for that range.</Alert>
            ) : (
                <Alert severity="success">Downloaded {lastCount.toLocaleString()} email(s).</Alert>
            )}
        </>
    );
}
